/**
 * Selection is a list of object references plus an optional sub-selection (a vertex, an edge
 * bend handle, or a checkpoint flag). The sub-selection only applies when exactly one object is
 * selected: dragging a single vertex is a different gesture from moving five objects at once, and
 * keeping the two in one structure is what lets the editor tell them apart.
 */

import { poleIntersectsRect } from './checkpointLook.ts';
import { resolveElevationStack } from './elevationStack.ts';
import { extrudeOffset, pointInColumn } from './extrude.ts';
import { pointInPolygon } from './islands.ts';
import { tessellateRing } from './ring.ts';
import { routeIntersectsRect } from './routes.ts';
import type { CheckpointFlag, Island, MapDocument, Ring, Rock, Vec2 } from './types.ts';

export type ObjectKind =
  | 'island'
  | 'rock'
  | 'water'
  | 'startArea'
  | 'parkArea'
  | 'checkpoint'
  | 'route';

export type ObjectRef =
  | { kind: 'island'; id: string }
  | { kind: 'rock'; id: string }
  | { kind: 'water'; id: string }
  | { kind: 'startArea' }
  | { kind: 'parkArea' }
  | { kind: 'checkpoint'; id: string }
  | { kind: 'route'; id: string };

export type SubSelection =
  | { kind: 'none' }
  | { kind: 'vertex'; index: number }
  | { kind: 'edge'; index: number }
  | { kind: 'flag'; flag: CheckpointFlag };

export type Selection = {
  refs: ObjectRef[];
  sub: SubSelection;
};

export type Rect = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

export const NO_SUB: SubSelection = { kind: 'none' };

/** Start / park are singletons, so their refs carry no id and the key is a bare kind. */
export function refKey(ref: ObjectRef): string {
  return ref.kind === 'startArea' || ref.kind === 'parkArea' ? ref.kind : `${ref.kind}:${ref.id}`;
}

export function refsEqual(a: ObjectRef, b: ObjectRef): boolean {
  return refKey(a) === refKey(b);
}

export function emptySelection(): Selection {
  return { refs: [], sub: NO_SUB };
}

export function selectionOf(ref: ObjectRef, sub: SubSelection = NO_SUB): Selection {
  return { refs: [ref], sub };
}

export function selectionOfMany(refs: readonly ObjectRef[]): Selection {
  return { refs: dedupe(refs), sub: NO_SUB };
}

export function isEmpty(selection: Selection): boolean {
  return selection.refs.length === 0;
}

export function hasRef(selection: Selection, ref: ObjectRef): boolean {
  return selection.refs.some((candidate) => refsEqual(candidate, ref));
}

/** The single selected object, or null when nothing or several things are selected. */
export function soleRef(selection: Selection): ObjectRef | null {
  return selection.refs.length === 1 ? (selection.refs[0] ?? null) : null;
}

/** The sub-selection, but only when `ref` is the one and only selected object. */
export function subFor(selection: Selection, ref: ObjectRef): SubSelection {
  const sole = soleRef(selection);
  return sole && refsEqual(sole, ref) ? selection.sub : NO_SUB;
}

export function selectedVertexIndex(selection: Selection, ref: ObjectRef): number | null {
  const sub = subFor(selection, ref);
  return sub.kind === 'vertex' ? sub.index : null;
}

export function selectedEdgeIndex(selection: Selection, ref: ObjectRef): number | null {
  const sub = subFor(selection, ref);
  return sub.kind === 'edge' ? sub.index : null;
}

export function selectedFlag(selection: Selection, ref: ObjectRef): CheckpointFlag | null {
  const sub = subFor(selection, ref);
  return sub.kind === 'flag' ? sub.flag : null;
}

/**
 * Drops a vertex or edge focus so Delete / Straighten cannot act on hidden
 * knobs. Checkpoint flag subs stay.
 */
export function withoutRingSub(selection: Selection): Selection {
  if (selection.sub.kind === 'vertex' || selection.sub.kind === 'edge') {
    return { refs: selection.refs, sub: NO_SUB };
  }

  return selection;
}

/**
 * Poles that should draw as selected. A lone flag sub-selection is just that pole; a whole
 * checkpoint (no sub, or part of a multi-selection) is both poles.
 */
export function selectedCheckpointPoles(selection: Selection, checkpointId: string): CheckpointFlag[] {
  const ref = { kind: 'checkpoint', id: checkpointId } as const;

  if (!hasRef(selection, ref)) {
    return [];
  }

  const flag = selectedFlag(selection, ref);
  return flag ? [flag] : ['a', 'b'];
}

/** Ids of every selected object of one kind, for applying an inspector edit to the whole selection. */
export function selectedIds(selection: Selection, kind: ObjectKind): string[] {
  return selection.refs
    .filter((ref): ref is Extract<ObjectRef, { id: string }> => ref.kind === kind && 'id' in ref)
    .map((ref) => ref.id);
}

export function kindsIn(selection: Selection): ObjectKind[] {
  return [...new Set(selection.refs.map((ref) => ref.kind))];
}

/** Shift/ctrl-click: adds an unselected object, removes an already-selected one. */
export function toggleRef(selection: Selection, ref: ObjectRef): Selection {
  if (hasRef(selection, ref)) {
    return { refs: selection.refs.filter((candidate) => !refsEqual(candidate, ref)), sub: NO_SUB };
  }

  return { refs: [...selection.refs, ref], sub: NO_SUB };
}

export function addRefs(selection: Selection, refs: readonly ObjectRef[]): Selection {
  return { refs: dedupe([...selection.refs, ...refs]), sub: NO_SUB };
}

function dedupe(refs: readonly ObjectRef[]): ObjectRef[] {
  const seen = new Set<string>();
  const out: ObjectRef[] = [];

  for (const ref of refs) {
    const key = refKey(ref);

    if (!seen.has(key)) {
      seen.add(key);
      out.push(ref);
    }
  }

  return out;
}

export function refExists(map: MapDocument, ref: ObjectRef): boolean {
  switch (ref.kind) {
    case 'island':
      return map.islands.some((item) => item.id === ref.id);
    case 'rock':
      return map.rocks.some((item) => item.id === ref.id);
    case 'water':
      return map.shallowWater.some((item) => item.id === ref.id);
    case 'startArea':
      return map.startArea !== null;
    case 'parkArea':
      return map.parkArea !== null;
    case 'checkpoint':
      return map.checkpoints.some((item) => item.id === ref.id);
    case 'route':
      return map.routes.some((item) => item.id === ref.id);
  }
}

/** Drops references to objects that no longer exist, e.g. after an undo or a delete. */
export function pruneSelection(selection: Selection, map: MapDocument): Selection {
  const refs = selection.refs.filter((ref) => refExists(map, ref));

  if (refs.length === selection.refs.length) {
    return selection;
  }

  return { refs, sub: refs.length === 1 ? selection.sub : NO_SUB };
}

export function normalizeRect(a: Vec2, b: Vec2): Rect {
  return {
    minX: Math.min(a.x, b.x),
    minY: Math.min(a.y, b.y),
    maxX: Math.max(a.x, b.x),
    maxY: Math.max(a.y, b.y),
  };
}

export function rectContainsPoint(rect: Rect, point: Vec2): boolean {
  return point.x >= rect.minX && point.x <= rect.maxX && point.y >= rect.minY && point.y <= rect.maxY;
}

export function rectArea(rect: Rect): number {
  return (rect.maxX - rect.minX) * (rect.maxY - rect.minY);
}

function ringIntersectsRect(ring: Ring, rect: Rect): boolean {
  const outline = tessellateRing(ring);

  if (outline.some((point) => rectContainsPoint(rect, point))) {
    return true;
  }

  // A marquee drawn entirely inside a large island should still select it.
  return pointInPolygon({ x: rect.minX, y: rect.minY }, outline);
}

function elevatedLandIntersectsRect(body: Island | Rock, map: MapDocument, rect: Rect): boolean {
  if (ringIntersectsRect(body, rect)) {
    return true;
  }

  const resolved = resolveElevationStack(map).get(body.id);
  const world = resolved?.worldElevation ?? body.elevation;
  const base = resolved?.baseElevation ?? 0;

  if (world <= 0) {
    return false;
  }

  const outline = tessellateRing(body);
  const offset = extrudeOffset(world);
  const plateau = outline.map((point) => ({ x: point.x + offset.x, y: point.y + offset.y }));

  if (plateau.some((point) => rectContainsPoint(rect, point))) {
    return true;
  }

  if (plateau.length >= 3 && pointInPolygon({ x: rect.minX, y: rect.minY }, plateau)) {
    return true;
  }

  const samples: Vec2[] = [
    { x: rect.minX, y: rect.minY },
    { x: rect.maxX, y: rect.minY },
    { x: rect.maxX, y: rect.maxY },
    { x: rect.minX, y: rect.maxY },
    { x: (rect.minX + rect.maxX) / 2, y: (rect.minY + rect.maxY) / 2 },
  ];

  return samples.some((point) => pointInColumn(outline, world, point, base));
}

function segmentIntersectsRect(a: Vec2, b: Vec2, rect: Rect): boolean {
  if (rectContainsPoint(rect, a) || rectContainsPoint(rect, b)) {
    return true;
  }

  // Cheap reject, then a coarse sample along the segment; gates are short enough for this to hold.
  if (
    Math.max(a.x, b.x) < rect.minX ||
    Math.min(a.x, b.x) > rect.maxX ||
    Math.max(a.y, b.y) < rect.minY ||
    Math.min(a.y, b.y) > rect.maxY
  ) {
    return false;
  }

  const steps = 16;

  for (let i = 1; i < steps; i += 1) {
    const t = i / steps;

    if (rectContainsPoint(rect, { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t })) {
      return true;
    }
  }

  return false;
}

/** Everything a marquee drag touches, in a stable order: water, land, start area, gates. */
export function refsInRect(
  map: MapDocument,
  rect: Rect,
  poleScale = 1,
  elevate = false,
  hiddenRouteIds?: ReadonlySet<string>,
): ObjectRef[] {
  const refs: ObjectRef[] = [];

  for (const zone of map.shallowWater) {
    if (ringIntersectsRect(zone, rect)) {
      refs.push({ kind: 'water', id: zone.id });
    }
  }

  for (const island of map.islands) {
    if (elevate ? elevatedLandIntersectsRect(island, map, rect) : ringIntersectsRect(island, rect)) {
      refs.push({ kind: 'island', id: island.id });
    }
  }

  for (const rock of map.rocks) {
    if (elevate ? elevatedLandIntersectsRect(rock, map, rect) : ringIntersectsRect(rock, rect)) {
      refs.push({ kind: 'rock', id: rock.id });
    }
  }

  if (map.startArea && ringIntersectsRect(map.startArea, rect)) {
    refs.push({ kind: 'startArea' });
  }

  if (map.parkArea && ringIntersectsRect(map.parkArea, rect)) {
    refs.push({ kind: 'parkArea' });
  }

  for (const checkpoint of map.checkpoints) {
    if (
      segmentIntersectsRect(checkpoint.a, checkpoint.b, rect) ||
      poleIntersectsRect(checkpoint.a, rect, poleScale) ||
      poleIntersectsRect(checkpoint.b, rect, poleScale)
    ) {
      refs.push({ kind: 'checkpoint', id: checkpoint.id });
    }
  }

  for (const route of map.routes) {
    if (hiddenRouteIds?.has(route.id)) {
      continue;
    }

    if (routeIntersectsRect(route, rect)) {
      refs.push({ kind: 'route', id: route.id });
    }
  }

  return refs;
}

/** Axis-aligned bounds of one object, or null if it no longer exists. */
export function refBounds(map: MapDocument, ref: ObjectRef): Rect | null {
  const points = refPoints(map, ref);

  if (!points || points.length === 0) {
    return null;
  }

  let rect: Rect = {
    minX: Number.POSITIVE_INFINITY,
    minY: Number.POSITIVE_INFINITY,
    maxX: Number.NEGATIVE_INFINITY,
    maxY: Number.NEGATIVE_INFINITY,
  };

  for (const point of points) {
    rect = {
      minX: Math.min(rect.minX, point.x),
      minY: Math.min(rect.minY, point.y),
      maxX: Math.max(rect.maxX, point.x),
      maxY: Math.max(rect.maxY, point.y),
    };
  }

  return rect;
}

export function selectionBounds(map: MapDocument, refs: readonly ObjectRef[]): Rect | null {
  let combined: Rect | null = null;

  for (const ref of refs) {
    const rect = refBounds(map, ref);

    if (!rect) {
      continue;
    }

    combined = combined
      ? {
          minX: Math.min(combined.minX, rect.minX),
          minY: Math.min(combined.minY, rect.minY),
          maxX: Math.max(combined.maxX, rect.maxX),
          maxY: Math.max(combined.maxY, rect.maxY),
        }
      : rect;
  }

  return combined;
}

function refPoints(map: MapDocument, ref: ObjectRef): readonly Vec2[] | null {
  switch (ref.kind) {
    case 'island':
      return map.islands.find((item) => item.id === ref.id)?.vertices ?? null;
    case 'rock':
      return map.rocks.find((item) => item.id === ref.id)?.vertices ?? null;
    case 'water':
      return map.shallowWater.find((item) => item.id === ref.id)?.vertices ?? null;
    case 'startArea':
      return map.startArea?.vertices ?? null;
    case 'parkArea':
      return map.parkArea?.vertices ?? null;
    case 'checkpoint': {
      const checkpoint = map.checkpoints.find((item) => item.id === ref.id);
      return checkpoint ? [checkpoint.a, checkpoint.b] : null;
    }
    case 'route':
      return map.routes.find((item) => item.id === ref.id)?.points ?? null;
  }
}
